/**
 * Character Behavior Registration
 * 
 * Wraps existing CharacterConfig definitions and registers them as behaviors.
 * This allows characters to be looked up via BehaviorRegistry.get('character:X').
 * 
 * The CharacterConfig structure is preserved - behaviors just wrap it.
 */

import { BehaviorRegistry, type EntityBehavior } from '../../BehaviorRegistry';
import { EntityType } from '../../../types/EntityTypes';
import type { CharacterConfig } from '../../../config/characters';

/**
 * Extended behavior that includes full CharacterConfig
 */
export interface CharacterConfigBehavior extends EntityBehavior {
  /** Full character config (animations, attacks, lifecycle, etc.) */
  characterConfig: CharacterConfig;
}

/**
 * Register a CharacterConfig as a behavior in the registry.
 * The behavior ID will be 'character:{config.id}'.
 * 
 * @param config The CharacterConfig to register
 */
export function registerCharacterBehavior(config: CharacterConfig): void {
  const behaviorId = `character:${config.id}`;
  
  const behavior: CharacterConfigBehavior = {
    // Behavior identity
    id: behaviorId,
    displayName: config.name,
    type: EntityType.CHARACTER,
    category: 'character',
    tags: ['character', config.id],
    
    // Store the full config for CharacterLoader to use
    characterConfig: config,
  };
  
  BehaviorRegistry.register(behavior);
}

/**
 * Get a CharacterConfig from a registered behavior.
 * 
 * @param characterId The character ID (e.g., 'lilly', not 'character:lilly')
 * @returns The CharacterConfig or undefined if not found
 */
export function getCharacterConfig(characterId: string): CharacterConfig | undefined {
  const behaviorId = characterId.startsWith('character:') 
    ? characterId 
    : `character:${characterId}`;
  
  const behavior = BehaviorRegistry.getDirect(behaviorId);
  if (!behavior) return undefined;
  
  // Cast to CharacterConfigBehavior to access characterConfig
  const charBehavior = behavior as unknown as CharacterConfigBehavior;
  return charBehavior.characterConfig;
}

/**
 * Check if a character behavior is registered
 */
export function hasCharacterBehavior(characterId: string): boolean {
  const behaviorId = characterId.startsWith('character:') 
    ? characterId 
    : `character:${characterId}`;
  return BehaviorRegistry.has(behaviorId);
}
